import React, {useState} from 'react'
import {Tooltip, Modal} from 'antd'
import {ColumnsType} from 'antd/es/table'
import {
  CalendarDaysIcon,
  LinkIcon,
  NewspaperIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import {useMutation, useQuery} from '@apollo/client'
import {GET_DEAL_BY_ID} from '../../utils/api/preferredDeal'
import Table from '../../components/lists/Table/Table'
import {useNavigate, useOutletContext} from 'react-router-dom'
import WebsitesForm, { confirmNewWebsites, getFloorPriceProperty } from '../../components/forms/Campaign/WebsitesForm'
import {UPDATE_CAMPAIGN} from '../../utils/api/campaign'
import PublicationStatus from '../../components/badges/PublicationStatus'
import {useCurrentUser} from '../../utils/CurrentUserContext'
import {DateRangeCell} from '../../components/lists/Table/shared/DateCell'
import RateCell from '../../components/lists/Table/shared/RateCell'
import {
  CampaignStatus,
  CampaignType,
  Publication,
  Website,
} from '../../__generated__/graphql'
import MarketplaceGuard from '../../components/forms/Campaign/MarketplaceGuard'
import {CAMPAIGN_PUBLICATIONS_QUERY} from '../../utils/api/publication'
import {useQueryVariables} from '../../utils/hooks'
import ActionButton, {
  ActionHandler,
} from '../../components/lists/Table/shared/ActionButton'
import {useTeaserExtensionPrompt} from '../../components/forms/Campaign/useTeaserExtensionPrompt'

const getColumns = (
  showBidMultiplier: boolean,
  campaignType: CampaignType,
  defaultBid: number,
  onDelete?: ActionHandler<string[]>
): ColumnsType<Publication> => [
  {
    key: 'website',
    title: 'Website',
    dataIndex: ['website', 'name'],
    render: (name: string, website) => {
      const icons = [
        {
          Icon: CalendarDaysIcon,
          title: 'Period is modified for this website',
          show: website.startDateOverwritten || website.endDateOverwritten,
        },
        {
          Icon: LinkIcon,
          title: 'Landing Page is modified for this website',
          show: website.landingPageOverwritten,
        },
        {
          Icon: NewspaperIcon,
          title: 'Content is modified for this website',
          show: website.contentOverwritten,
        },
      ]
      return (
        <>
          {name}
          {icons.map(({Icon, title, show}) => {
            if (!show) {
              return null
            }

            return (
              <Tooltip placement='bottom' title={title}>
                <Icon className='ml-2 h-5 w-5 inline' />
              </Tooltip>
            )
          })}
        </>
      )
    },
  },
  {
    key: 'status',
    title: 'Status',
    dataIndex: 'status',
    render: (text) => <PublicationStatus status={text} />,
  },
  {
    key: 'rateCard',
    title: 'Bid Price',
    dataIndex: ['rateCard'],
    render: (rateCard, record) => (
      <RateCell
        website={record.website}
        campaignType={campaignType}
        defaultBid={defaultBid}
        multiplier={rateCard.bidMultiplier}
        publicationId={record._id}
      />
    ),
    hidden: !showBidMultiplier,
  },
  {
    key: 'period',
    title: 'Period',
    render: (_: any, record) => (
      <DateRangeCell start={record.startDate} end={record.endDate} />
    ),
  },
  ...(onDelete
    ? ([
        {
          key: 'actions',
          width: 40,
          render: (_, row) => (
            <ActionButton
              tooltip='Delete'
              row={[row._id]}
              onClick={onDelete}
              Icon={TrashIcon}
              danger
            />
          ),
        },
      ] as ColumnsType<Publication>)
    : []),
]

const Websites = () => {
  const {user} = useCurrentUser()
  const {campaign, marketplace} = useOutletContext<any>()

  const {data: dealData} = useQuery(GET_DEAL_BY_ID, {
    variables: {id: campaign.dealId},
    skip: !campaign.dealId,
  })
  const deal = dealData?.deal

  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const [newSelectedWebsitesIds, setNewSelectedWebsitesIds] = useState<string[]>([])
  const [selectedWebsites, setSelectedWebsites] = useState<Website[]>([])
  const {promptTeaserExtension, teaserExtensionModal} = useTeaserExtensionPrompt()

  const {variables, setPagination, setFilters} = useQueryVariables({
    campaignId: campaign._id,
  })

  const {loading, data} = useQuery(CAMPAIGN_PUBLICATIONS_QUERY, {
    variables,
  })

  const publications: Publication[] = data?.publications.items || []
  const totalItems = data?.publications.meta.totalItems || 0

  const [updateCampaign, {loading: saving}] = useMutation(UPDATE_CAMPAIGN, {
    refetchQueries: ['PublicationMany', 'TeaserMany'],
  })

  const campaignOwner = campaign.ownerCompanyId === user.company?._id

  const navigate = useNavigate()

  const handleRowClicked = (row: Publication) => {
    navigate(`/${marketplace ? 'marketplace-campaigns' : 'campaigns'}/${campaign._id}/publications/${row._id}/details`)
  }

  const handleAddWebsites = async () => {
    const update = async () => {
      const newPublications = newSelectedWebsitesIds.map((websiteId) => ({websiteId}))

      const currentWebsiteIds = campaign.publications.map(({websiteId}: any) => websiteId)
      const newWebsiteIds = newSelectedWebsitesIds.filter((id) => !currentWebsiteIds.includes(id))

      await updateCampaign({
        variables: {id: campaign._id, record: {publications: newPublications}},
      })

      await promptTeaserExtension({
        campaignId: campaign._id,
        currentWebsiteIds,
        newWebsiteIds,
      })

      setModalVisible(false)
    }

    const currentWebsiteIds = campaign.publications.map(({websiteId}: any) => websiteId)

    confirmNewWebsites(user, selectedWebsites, campaign, update, currentWebsiteIds)
  }

  const handleDeletePublications = async (ids: string[]) => {
    const deletePublications = async () => {
      const newPublications = (campaign.publications as Publication[])
        .filter(({_id}) => !ids.includes(_id))
        .map(({websiteId}) => ({websiteId}))

      if (publications.length === 1 && variables.page && variables.page > 1) {
        setPagination({
          current: variables.page - 1,
          pageSize: variables.perPage,
        })
      }

      return updateCampaign({
        variables: {id: campaign._id, record: {publications: newPublications}},
      })
    }

    Modal.confirm({
      title: 'Are you sure?',
      content:
        'Deleting a website will also delete the article and the teasers.',
      okText: 'Delete',
      okType: 'danger',
      onOk: deletePublications,
    })
  }

  const showModal = () => {
    const selectedWebsites = campaign.publications.map(({websiteId}: any) => websiteId)

    setNewSelectedWebsitesIds(selectedWebsites)
    setSelectedWebsites([])
    setModalVisible(true)
  }

  const tableProps =
    campaignOwner && campaign.status !== CampaignStatus.Finished
      ? {
          actionButtonText: 'Add Website',
          onActionButtonClick: showModal,
        }
      : {}
  const allowDeletePublications =
    campaignOwner &&
    ![CampaignStatus.Active, CampaignStatus.Finished].includes(campaign.status)

  return (
    <div className='flex-1 relative bg-white rounded-md px-4 py-4 pb-20 mb-10 space-y-6 sm:space-y-5'>
      <MarketplaceGuard>
        <Table
          columns={getColumns(
            campaignOwner &&
              [
                CampaignType.Standard,
                CampaignType.MarketplaceNativeDisplay,
              ].includes(campaign.type),
            campaign.type,
            campaign.rateCard?.price || 0,
            allowDeletePublications ? handleDeletePublications : undefined
          )}
          data={publications}
          onRowClicked={handleRowClicked}
          loading={loading}
          pagination={{
            total: totalItems,
            pageSize: variables.perPage,
            current: variables.page,
          }}
          onChange={(newPagination, newFilters) => {
            setPagination(newPagination)
            setFilters({...variables.filters, ...newFilters})
          }}
          {...tableProps}
        />
      </MarketplaceGuard>

      <Modal
        open={modalVisible}
        title='Add websites'
        okText='Add'
        okButtonProps={{
          loading: saving,
          disabled: publications.length === newSelectedWebsitesIds.length,
        }}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: '75vh', overflowY: 'auto' }}
        onCancel={() => setModalVisible(false)}
        onOk={handleAddWebsites}
        destroyOnClose
      >
        <WebsitesForm
          selectedWebsites={newSelectedWebsitesIds}
          onChange={(selectedWebsiteIds, selectedWebsites) => {
            setNewSelectedWebsitesIds(selectedWebsiteIds)
            setSelectedWebsites(selectedWebsites)
          }}
          filters={{
            marketplace: campaign.marketplace,
            campaignType: campaign.type,
            contentSource: campaign.contentSource,
            ...(deal?.websiteIds ? {websiteIds: deal.websiteIds} : {}),
          }}
          getCheckboxProps={(website) => ({
            disabled: !!campaign.publications.find(
              (publication: any) => publication.websiteId === website._id
            ),
          })}
        />
      </Modal>

      {teaserExtensionModal}
    </div>
  )
}

export default Websites
